import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, PageHeader, Switch } from 'antd';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import { ProCoreActionType } from '@ant-design/pro-utils';
import { FormInstance } from 'antd/lib/form';
import { usePageState } from '../../core/hooks/usePageState';
import CoreProTable, { CoreProTableState } from '../../core/components/CoreProTable';
import { UserWallet, UserWalletProvider } from '../../domain/usdt-providers/userWalletProvider';
import { ProColumns } from '@ant-design/pro-table';
import PaginationTransformer from '../../core/utils/paginationTransformer';
import { transfer2LocalTime } from '../../core/utils/timeFormat';
import CreateWalletForm from '../../domain/components/setttings/CreateWalletForm';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ChannelValueEnum, MchToken } from '../../domain/channel/channel';
import HexLabel from '../../core/components/HexLabel';
import _module from '../configs/module';
import { ADD_POLLING_WALLET } from '../configs/scopes';

interface PageOp extends CoreProTableState<UserWallet> {
}

// 可拖拉調整寬度的表頭 cell（antd 官方 resizable columns 做法）
const ResizableTitle = (
  props: React.HTMLAttributes<any> & {
    onResize: (e: React.SyntheticEvent, data: ResizeCallbackData) => void;
    width: number;
  },
) => {
  const { onResize, width, ...restProps } = props;
  if (!width) {
    return <th {...restProps} />;
  }
  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className='react-resizable-handle'
          style={{
            position: 'absolute',
            right: -5,
            bottom: 0,
            zIndex: 1,
            width: 10,
            height: '100%',
            cursor: 'col-resize',
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} style={{ ...restProps.style, position: 'relative' }} />
    </Resizable>
  );
};

// eslint-disable-next-line
export default function UserWalletList() {
  const actionRef = useRef<ProCoreActionType>();
  const searchFormRef = useRef<FormInstance>();
  const { t } = useTranslation();
  const pageOp = usePageState<PageOp>('PAGE_REMEMBER', {
    params: {},
    collapsed: false,
    pagination: {
      pageSize: 10,
      current: 1,
    },
    needRequest: true,
  });
  const getAvailable = (available: any) => {
    if (available) {
      return <Switch checked={true} disabled={true} />;
    } else {
      return <Switch checked={false} disabled={true} />;
    }
  };
  const [createWalletVisible, setCreateWalletVisible] = useState(false);
  // 各欄被拖拉後的寬度覆寫（key = 欄位索引）
  const [widthOverrides, setWidthOverrides] = useState<Record<number, number>>(
    {},
  );
  useEffect(() => {
    searchFormRef.current?.setFieldsValue(pageOp.get().params);
  }, [pageOp, searchFormRef]);
  const CollectWalletCommonColumn: ProColumns<UserWallet>[] = [
    {
      hideInSearch: false,
      hideInTable: false,
      width: 100,
      align: 'center',
      title: t('payment.wallet_id'),
      fixed: 'left',
      dataIndex: 'userId',
    },
    {
      align: 'center',
      title: t('payment.channel'),
      dataIndex: 'channel',
      valueType: 'select',
      valueEnum: ChannelValueEnum,
      onFilter: true,
      width: 70,
    },
    {
      align: 'center',
      title: t('payment.token'),
      dataIndex: 'token',
      valueType: 'select',
      valueEnum: MchToken,
      onFilter: true,
      width: 70,
    },
    {
      hideInSearch: false,
      hideInTable: false,
      width: 100,
      align: 'center',
      title: t('payment.wallet_address'),
      dataIndex: 'walletAddress',
      render: (n, row) => <HexLabel hex={row.walletAddress} size={'small'} />,
    },
    {
      hideInSearch: true,
      hideInTable: false,
      width: 100,
      align: 'center',
      title: t('payment.available'),
      dataIndex: 'available',
      render: (row) => getAvailable(row),
    },
    {
      hideInSearch: true,
      hideInTable: true,
      width: 100,
      align: 'center',
      title: t('payment.txnBalance'),
      dataIndex: 'txnBalance',
    },
    {
      hideInSearch: true,
      hideInTable: false,
      width: 100,
      align: 'center',
      title: t('payment.availableBalance'),
      dataIndex: 'availableBalance',
    },
    {
      hideInSearch: true,
      hideInTable: false,
      width: 100,
      align: 'center',
      title: t('common.createdAt'),
      dataIndex: 'createdAt',
      render: (text: any, row) => transfer2LocalTime(new Date(row.createdAt)),
    },
  ];

  const disableUserWallet = (row: UserWallet) => {
    Modal.confirm({
      title: t('credit.confirm_disable_polling_wallet_message'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      centered: true,
      onOk: () => {
        return UserWalletProvider.disableWallet({
          walletAddress: row.walletAddress,
          channel: row.channel,
          token: row.token,
        }).then((res) => {
          pageOp.update((v) => ({ ...v, needRequest: true }));
          actionRef.current?.reload();
        });
      },
    });
  };
  const enableUserWallet = (row: UserWallet) => {
    Modal.confirm({
      title: t('credit.confirm_enable_polling_wallet_message'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      centered: true,
      onOk: () => {
        return UserWalletProvider.enableWallet({
          walletAddress: row.walletAddress,
          channel: row.channel,
          token: row.token,
        }).then((res) => {
          pageOp.update((v) => ({ ...v, needRequest: true }));
          actionRef.current?.reload();
        });
      },
    });
  };
  const actionColumn: ProColumns<UserWallet> = {
    title: '操作',
    key: '_action',
    align: 'center',
    dataIndex: 'id',
    valueType: 'option',
    width: 100,
    render: (text, row) =>
      !row.collectStatus || row.collectStatus === 'UnCollect'
        ? [
          <Button
            key={1}
            type={'primary'}
            size={'small'}
            disabled={!_module.can([ADD_POLLING_WALLET])}
            danger={row.available && _module.can([ADD_POLLING_WALLET])}
            onClick={() => {
              if (row.available) {
                disableUserWallet(row);
              } else {
                enableUserWallet(row);
              }
            }}
          >
            {row.available ? t('common.disable') : t('common.enable')}
          </Button>,
        ]
        : null,
  };

  // 為每一欄接上可拖拉 resize：目前寬度取覆寫值、拖拉時更新覆寫
  const resizableColumns: ProColumns<UserWallet>[] = [
    ...CollectWalletCommonColumn,
    actionColumn,
  ].map((col, index) => {
    const currentWidth = widthOverrides[index] ?? (col.width as number);
    return {
      ...col,
      width: currentWidth,
      onHeaderCell: (column: ProColumns<UserWallet>) =>
        ({
          width: (column as any).width,
          onResize: (
            _e: React.SyntheticEvent,
            data: ResizeCallbackData,
          ) => {
            setWidthOverrides((prev) => ({
              ...prev,
              [index]: data.size.width,
            }));
          },
        } as any),
    } as ProColumns<UserWallet>;
  });

  return (
    <>
      <PageHeader
        title={t('payment.polling_wallet')}
        subTitle=''
        extra={<></>}
      />
      <CoreProTable<UserWallet>
        formRef={searchFormRef}
        tableState={pageOp.get()}
        updateTableState={(state) => {
          pageOp.update((v) => ({ ...v, ...state }));
        }}
        scroll={{ x: 'fixed' }}
        components={{ header: { cell: ResizableTitle } }}
        search={{ layout: 'vertical' }}
        actionRef={actionRef as any}
        toolBarRender={() => [
          _module.canRender(
            ADD_POLLING_WALLET,
          <Button
            key={1}
            type='primary'
            onClick={() => setCreateWalletVisible(true)}
          >
            <PlusOutlined />
            {t('payment.create_wallet')}
          </Button>,
            )
        ]}
        rowKey='walletAddress'
        requestData={(params: any, sort, filter) => {
          if (params.status === 'ALL') {
            delete params.status;
          }
          return UserWalletProvider.mchGetUserWalletList({
            page: params.current,
            limit: params.pageSize,
            uid: params.userId,
            walletAddress: params.walletAddress,
            channel: params.channel,
            token: params.token,
          }).then((res) => {
            return PaginationTransformer.responseTransfer(res);
          });
        }}
        columns={resizableColumns}
      />
      <CreateWalletForm
        visible={createWalletVisible}
        onClosed={() => {
          setCreateWalletVisible(false);
          pageOp.update((v) => ({ ...v, needRequest: true }));
          actionRef.current?.reload();
        }}
      />
    </>
  );
}
